import { z } from 'zod'

// Contrat runtime partagé : aucune dépendance UI, Supabase ou environnementale.

const identifierSchema = z.uuid()
const dateTimeSchema = z.iso.datetime({ offset: true })
const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const positiveVersionSchema = z.number().int().positive()

export const OPERATOR_SCHEMA_VERSION = 1 as const
export const OPERATOR_SYNC_BATCH_MAX_OPERATIONS = 100 as const

export const missionStatusSchema = z.enum([
  'PLANNED',
  'READY',
  'IN_PROGRESS',
  'PAUSED',
  'COMPLETED',
  'CANCELLED',
])

export const missionItemStatusSchema = z.enum([
  'WAITING',
  'EN_ROUTE',
  'APPROACHING',
  'IN_PROGRESS',
  'COMPLETED',
  'PROBLEM',
  'SKIPPED',
  'CANCELLED',
])

export const transitionSourceSchema = z.enum(['GPS', 'OPERATOR', 'DISPATCHER', 'ADMINISTRATIVE_CORRECTION'])

export const postalAddressSchema = z
  .object({
    line1: z.string().min(1),
    line2: z.string().min(1).optional(),
    city: z.string().min(1),
    province: z.string().min(1),
    postalCode: z.string().min(1),
    countryCode: z.string().length(2),
  })
  .strict()

export const geoPointSchema = z
  .object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
  })
  .strict()

const geoJsonPositionSchema = z.tuple([z.number(), z.number()])
const geoJsonLinearRingSchema = z
  .array(geoJsonPositionSchema)
  .min(4)
  .refine((positions) => {
    const first = positions[0]
    const last = positions.at(-1)
    if (first === undefined || last === undefined) return false
    return first[0] === last[0] && first[1] === last[1]
  }, 'Un anneau GeoJSON doit être fermé')
const geoJsonPolygonCoordinatesSchema = z.array(geoJsonLinearRingSchema).min(1)

export const geoJsonPolygonSchema = z
  .object({
    type: z.literal('Polygon'),
    coordinates: geoJsonPolygonCoordinatesSchema,
  })
  .strict()

export const geoJsonMultiPolygonSchema = z
  .object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(geoJsonPolygonCoordinatesSchema).min(1),
  })
  .strict()

export const operatorMissionAlertSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
    severity: z.enum(['INFO', 'WARNING', 'BLOCKING']),
  })
  .strict()

export const operatorMissionSchema = z
  .object({
    schemaVersion: z.literal(OPERATOR_SCHEMA_VERSION),
    id: identifierSchema,
    number: z.string().min(1),
    status: missionStatusSchema,
    scheduledDate: localDateSchema,
    scheduledStartAt: dateTimeSchema.optional(),
    operator: z
      .object({
        employeeId: identifierSchema,
        displayName: z.string().min(1),
      })
      .strict(),
    equipment: z
      .object({
        equipmentId: identifierSchema,
        number: z.string().min(1),
        name: z.string().min(1),
        type: z.string().min(1),
      })
      .strict()
      .optional(),
    route: z
      .object({
        routeId: identifierSchema,
        number: z.string().min(1),
        name: z.string().min(1),
        /** Consigne de la route pour les opérateurs, figée à la génération de la Mission (absente si aucune). */
        description: z.string().min(1).max(500).optional(),
        sourceVersion: positiveVersionSchema.optional(),
      })
      .strict()
      .optional(),
    totalItems: z.number().int().nonnegative(),
    missionVersion: positiveVersionSchema,
    geometryVersion: positiveVersionSchema.optional(),
    algorithmVersion: z.string().min(1).optional(),
    startedAt: dateTimeSchema.optional(),
    pausedAt: dateTimeSchema.optional(),
    completedAt: dateTimeSchema.optional(),
    instructions: z.string().min(1).optional(),
    publishedAt: dateTimeSchema,
  })
  .strict()

export const operatorMissionItemSchema = z
  .object({
    id: identifierSchema,
    missionId: identifierSchema,
    contractId: identifierSchema.optional(),
    order: z.number().int().nonnegative(),
    status: missionItemStatusSchema,
    contractNumber: z.string().min(1),
    clientDisplayName: z.string().min(1),
    clientPhone: z.string().min(1).optional(),
    serviceAddress: postalAddressSchema,
    location: geoPointSchema,
    snowGeometry: geoJsonMultiPolygonSchema.optional(),
    gpsGeometry: z.union([geoJsonPolygonSchema, geoJsonMultiPolygonSchema]).optional(),
    geometryVersion: positiveVersionSchema.optional(),
    areaSquareMeters: z.number().nonnegative().optional(),
    operatorMessage: z.string().min(1).optional(),
    knownObstacles: z.string().min(1).optional(),
    specialInstructions: z.string().min(1).optional(),
    alerts: z.array(operatorMissionAlertSchema),
    travelStartedAt: dateTimeSchema.optional(),
    approachStartedAt: dateTimeSchema.optional(),
    workStartedAt: dateTimeSchema.optional(),
    completedAt: dateTimeSchema.optional(),
    travelDurationSeconds: z.number().int().nonnegative().optional(),
    approachDurationSeconds: z.number().int().nonnegative().optional(),
    workDurationSeconds: z.number().int().nonnegative().optional(),
    problemCount: z.number().int().nonnegative(),
    itemVersion: positiveVersionSchema,
  })
  .strict()

export const operatorMissionPayloadV1Schema = z
  .object({
    schemaVersion: z.literal(OPERATOR_SCHEMA_VERSION),
    generatedAt: dateTimeSchema,
    mission: operatorMissionSchema,
    items: z.array(operatorMissionItemSchema).min(1),
  })
  .strict()
  .superRefine((payload, context) => {
    if (payload.mission.totalItems !== payload.items.length) {
      context.addIssue({
        code: 'custom',
        message: 'mission.totalItems doit correspondre au nombre de MissionItems',
        path: ['mission', 'totalItems'],
      })
    }

    for (const [index, item] of payload.items.entries()) {
      if (item.missionId !== payload.mission.id) {
        context.addIssue({
          code: 'custom',
          message: 'Le MissionItem doit appartenir à la Mission du payload',
          path: ['items', index, 'missionId'],
        })
      }
    }
  })

export const missionItemTransitionPayloadSchema = z
  .object({
    fromStatus: missionItemStatusSchema,
    toStatus: missionItemStatusSchema,
    source: transitionSourceSchema,
  })
  .strict()

export const operatorProblemPayloadSchema = z
  .object({
    problemId: identifierSchema,
    missionItemId: identifierSchema.optional(),
    code: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
    severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'BLOCKING']),
    description: z.string().min(1).optional(),
    photoOperationIds: z.array(identifierSchema).optional(),
  })
  .strict()

export const syncOperationTypeSchema = z.enum([
  'MISSION_STARTED',
  'MISSION_PAUSED',
  'MISSION_RESUMED',
  'MISSION_COMPLETED',
  'MISSION_ITEM_TRANSITIONED',
  'PROBLEM_REPORTED',
])

const syncOperationBaseSchema = z.object({
  id: identifierSchema,
  idempotencyKey: z.string().min(1),
  missionId: identifierSchema,
  missionVersion: positiveVersionSchema,
  entityId: identifierSchema,
  sequence: z.number().int().positive(),
  occurredAt: dateTimeSchema,
})

export const syncOperationPayloadSchema = z.discriminatedUnion('operationType', [
  syncOperationBaseSchema
    .extend({
      entityType: z.literal('MISSION'),
      operationType: z.enum(['MISSION_STARTED', 'MISSION_PAUSED', 'MISSION_RESUMED', 'MISSION_COMPLETED']),
      payload: z.object({}).strict(),
    })
    .strict(),
  syncOperationBaseSchema
    .extend({
      entityType: z.literal('MISSION_ITEM'),
      operationType: z.literal('MISSION_ITEM_TRANSITIONED'),
      payload: missionItemTransitionPayloadSchema.extend({ itemVersion: positiveVersionSchema }),
    })
    .strict(),
  syncOperationBaseSchema
    .extend({
      entityType: z.literal('PROBLEM'),
      operationType: z.literal('PROBLEM_REPORTED'),
      payload: operatorProblemPayloadSchema,
    })
    .strict(),
])

export const operatorSyncBatchV1Schema = z
  .object({
    schemaVersion: z.literal(OPERATOR_SCHEMA_VERSION),
    operatorAppVersion: z.string().min(1),
    deviceId: identifierSchema,
    operatorId: identifierSchema,
    missionId: identifierSchema,
    firstSequence: z.number().int().positive(),
    lastSequence: z.number().int().positive(),
    operations: z.array(syncOperationPayloadSchema).min(1).max(OPERATOR_SYNC_BATCH_MAX_OPERATIONS),
    sentAt: dateTimeSchema,
  })
  .strict()
  .superRefine((batch, context) => {
    const sequences = batch.operations.map((operation) => operation.sequence)
    const expectedLength = batch.lastSequence - batch.firstSequence + 1

    if (
      batch.operations.some((operation) => operation.missionId !== batch.missionId) ||
      sequences.some((sequence, index) => sequence !== batch.firstSequence + index) ||
      expectedLength < 1 ||
      sequences.length !== expectedLength
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Le batch doit contenir une séquence continue et une seule Mission',
        path: ['operations'],
      })
    }

    if (
      new Set(batch.operations.map((operation) => operation.idempotencyKey)).size !== batch.operations.length
    ) {
      context.addIssue({
        code: 'custom',
        message: "Les clés d'idempotence doivent être uniques dans le batch",
        path: ['operations'],
      })
    }
  })

export const syncAcknowledgementSchema = z
  .object({
    operationId: identifierSchema,
    idempotencyKey: z.string().min(1),
    sequence: z.number().int().positive(),
    status: z.enum(['APPLIED', 'ALREADY_APPLIED', 'REJECTED', 'CONFLICT']),
    serverReceivedAt: dateTimeSchema,
    serverEntityVersion: positiveVersionSchema.optional(),
    errorCode: z.string().min(1).optional(),
    message: z.string().min(1).optional(),
  })
  .strict()

export const operatorSyncBatchResponseV1Schema = z
  .object({
    schemaVersion: z.literal(OPERATOR_SCHEMA_VERSION),
    serverReceivedAt: dateTimeSchema,
    acknowledgements: z.array(syncAcknowledgementSchema).min(1),
  })
  .strict()

export const operatorCompatibilitySchema = z
  .object({
    minimumSupportedVersion: z.string().min(1),
    recommendedVersion: z.string().min(1),
    blockedVersions: z.array(z.string().min(1)),
    supportedSchemaVersions: z.array(z.number().int().nonnegative()).min(1),
  })
  .strict()

export type MissionStatus = z.infer<typeof missionStatusSchema>
export type MissionItemStatus = z.infer<typeof missionItemStatusSchema>
export type OperatorMissionPayloadV1 = z.infer<typeof operatorMissionPayloadV1Schema>
export type SyncOperationPayload = z.infer<typeof syncOperationPayloadSchema>
export type OperatorSyncBatchV1 = z.infer<typeof operatorSyncBatchV1Schema>
export type SyncAcknowledgement = z.infer<typeof syncAcknowledgementSchema>
export type OperatorSyncBatchResponseV1 = z.infer<typeof operatorSyncBatchResponseV1Schema>
export type OperatorCompatibility = z.infer<typeof operatorCompatibilitySchema>
